import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import {
  Loader2, Plus, Trash2, Save, Calendar, Users, Award, Database,
  CheckCircle2, Pencil, X,
} from "lucide-react";
import { UniHeader, fmtSessionRange, usd } from "./_lmsShared";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const EVENT_TYPES = [
  { value: "continuing_education", label: "Continuing Education" },
  { value: "clinical_residency",   label: "Clinical Residency" },
  { value: "open_house",           label: "Open House" },
  { value: "workshop",             label: "Workshop" },
  { value: "seminar",              label: "Seminar" },
  { value: "guest_instructor",     label: "Guest Instructor" },
  { value: "other",                label: "Other" },
];

const DELIVERY_TYPES = [
  { value: "online_live", label: "Online · Live" },
  { value: "self_paced",  label: "Self-Paced" },
  { value: "in_person",   label: "In Person" },
  { value: "hybrid",      label: "Hybrid" },
];

const STATUSES = ["draft", "published", "cancelled", "completed"];
const TRACKS   = [
  { value: "",           label: "No track" },
  { value: "in_person",  label: "In-person residency" },
  { value: "online",     label: "Online residency" },
];

const EVENT_TYPE_LABEL = Object.fromEntries(EVENT_TYPES.map((t) => [t.value, t.label]));

// Defaults for a brand-new (blank) event of a given type — explicitly does NOT
// carry over any prior form data. Used both on first mount and on tab switch.
function blankEvent(eventType) {
  const t = eventType || "continuing_education";
  return {
    event_type: t,
    title: "",
    description: "",
    delivery_type: t === "clinical_residency" ? "in_person" : "online_live",
    instructor_name: "Cinthia Vonn",
    instructor_email: "",
    location: "",
    meeting_url: "",
    // Independent scheduling state — never overwrite each other
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
    sessions: [],   // multi-day support (Clinical Residency)
    price_usd: 0,
    seat_limit: 0,
    registration_deadline: "",
    ce_hours: 0,
    clinical_hours: 0,
    residency_hours: 0,
    track: t === "clinical_residency" ? "in_person" : "",
    includes_kit: t === "clinical_residency",
    status: "draft",
  };
}

// ─── ISO ⇄ split helpers ────────────────────────────────────────────────────
// Date and time are stored as separate strings throughout the form so that
// editing one CANNOT erase the other (independent React state).

function isoToDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function isoToTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${mi}`;
}
function joinDateTime(date, time) {
  if (!date) return "";
  // If time missing, default to 00:00 so we still return a valid date-only ISO.
  const t = time && /^\d{2}:\d{2}/.test(time) ? time : "00:00";
  // Construct local time then convert to ISO; back end stores ISO UTC.
  const local = new Date(`${date}T${t}:00`);
  if (Number.isNaN(local.getTime())) return "";
  return local.toISOString();
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export default function AdminUniversityEvents() {
  const loc = useLocation();
  const isResidencyRoute = loc.pathname.endsWith("/residency");
  const routeDefaultType = isResidencyRoute ? "clinical_residency" : "continuing_education";

  // The active tab also controls the default for new events. "all" falls back to
  // the route's natural default so the form stays sensible while showing every type.
  const [activeTab, setActiveTab] = useState(routeDefaultType);
  const formDefaultType = activeTab === "all" ? routeDefaultType : activeTab;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null); // { ...event } open in modal

  const load = async () => {
    setLoading(true);
    try {
      const params = activeTab === "all" ? {} : { event_type: activeTab };
      const r = await universityApi.eventsAdminList(params);
      setEvents(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load events"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTab]);

  const seedDefaults = async () => {
    try {
      const r = await universityApi.eventsAdminSeedDefaults();
      toast.success(r.data?.skipped ? "Already seeded" : `Seeded ${r.data?.created || 0} events`);
      await load();
    } catch (e) { toast.error(formatApiError(e, "Seed failed")); }
  };

  return (
    <AdminShell door="university" title="Events / CE Calendar">
      <div data-testid="admin-events-page">
        {/* Tabs */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-slate-500">Type</span>
            {["all", ...EVENT_TYPES.map((t) => t.value)].map((t) => (
              <button
                key={t}
                onClick={() => setActiveTab(t)}
                className={`text-xs px-2 py-1 rounded-lg border ${
                  activeTab === t
                    ? "border-purple-900 bg-purple-50 text-purple-900"
                    : "border-slate-200 text-slate-600 hover:border-purple-300"
                }`}
                data-testid={`admin-events-filter-${t}`}
              >
                {t === "all" ? "All" : (EVENT_TYPES.find((x) => x.value === t)?.label || t)}
              </button>
            ))}
          </div>
          <button
            onClick={seedDefaults}
            className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1"
            data-testid="admin-events-seed-btn"
          >
            <Database className="w-3 h-3" />Seed sample events
          </button>
        </div>

        {/* Create form — fully remounted whenever the tab changes so no prior
            form data ever bleeds across event types. */}
        <NewEventForm
          key={`new-${formDefaultType}`}
          defaultType={formDefaultType}
          onCreated={load}
        />

        {/* Event list */}
        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />Loading…
            </div>
          ) : events.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="admin-events-empty">
              No events yet for <b>{activeTab === "all" ? "any type" : EVENT_TYPE_LABEL[activeTab] || activeTab}</b>.
            </div>
          ) : events.map((e) => (
            <EventRow
              key={e.id}
              event={e}
              expanded={!!expanded[e.id]}
              onToggle={() => setExpanded((s) => ({ ...s, [e.id]: !s[e.id] }))}
              onEdit={() => setEditing(e)}
              reload={load}
            />
          ))}
        </div>

        {/* Edit modal */}
        {editing && (
          <EditEventModal
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </div>
    </AdminShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared form (used by both Create and Edit)
// ─────────────────────────────────────────────────────────────────────────────

function EventFormBody({ body, set, allowSessions }) {
  const showSessions = allowSessions && body.event_type === "clinical_residency";
  const hasSessions  = showSessions && (body.sessions || []).length > 0;

  // ── Session helpers (multi-day) ────────────────────────────────────────────
  const addSession = () => {
    set("sessions", [
      ...(body.sessions || []),
      { id: `s-${Date.now()}`, label: `Day ${(body.sessions?.length || 0) + 1}`, start_date: "", start_time: "", end_date: "", end_time: "" },
    ]);
  };
  const updateSession = (idx, k, v) => {
    const next = [...(body.sessions || [])];
    next[idx] = { ...next[idx], [k]: v };
    set("sessions", next);
  };
  const removeSession = (idx) => {
    const next = [...(body.sessions || [])];
    next.splice(idx, 1);
    set("sessions", next);
  };

  return (
    <div className="space-y-6" data-testid="event-form-body">
      {/* ─── Event Information ─────────────────────────────────────── */}
      <FormSection title="Event Information">
        <Field label="Event Name" required testid="ef-title">
          <input
            type="text"
            className="form-input"
            value={body.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="e.g. 2-Day Clinical Residency"
            data-testid="ef-title-input"
          />
        </Field>
        <Field label="Description" full testid="ef-description">
          <textarea
            className="form-input min-h-[96px]"
            rows={4}
            value={body.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Describe the event, what students will learn, and any prerequisites."
            data-testid="ef-description-input"
          />
        </Field>
        <Field label="Event Type" testid="ef-event-type">
          <select
            className="form-input"
            value={body.event_type}
            onChange={(e) => set("event_type", e.target.value)}
            data-testid="ef-event-type-select"
          >
            {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Delivery Format" testid="ef-delivery-format">
          <select
            className="form-input"
            value={body.delivery_type}
            onChange={(e) => set("delivery_type", e.target.value)}
            data-testid="ef-delivery-format-select"
          >
            {DELIVERY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
      </FormSection>

      {/* ─── Scheduling ────────────────────────────────────────────── */}
      <FormSection title="Scheduling">
        {showSessions && (
          <div className="md:col-span-2 rounded-xl border border-purple-100 bg-purple-50/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-medium text-purple-900">Multi-Day Sessions</div>
                <div className="text-xs text-slate-600">Use sessions for residencies that span multiple days. Single-day events leave this empty.</div>
              </div>
              <button
                type="button"
                onClick={addSession}
                className="rounded-lg border border-purple-300 bg-white text-purple-900 text-xs font-medium px-3 h-8 inline-flex items-center gap-1 hover:bg-purple-100"
                data-testid="ef-add-session-btn"
              >
                <Plus className="w-3 h-3" />Add Session
              </button>
            </div>

            {hasSessions ? (
              <div className="space-y-2">
                {body.sessions.map((s, idx) => (
                  <div key={s.id || idx} className="rounded-lg bg-white border border-slate-200 p-3" data-testid={`ef-session-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <input
                        type="text"
                        className="form-input h-8 text-xs flex-1 mr-2"
                        placeholder={`Day ${idx + 1}`}
                        value={s.label || ""}
                        onChange={(e) => updateSession(idx, "label", e.target.value)}
                        data-testid={`ef-session-${idx}-label`}
                      />
                      <button
                        type="button"
                        onClick={() => removeSession(idx)}
                        className="text-rose-600 hover:text-rose-700 text-xs inline-flex items-center gap-1"
                        data-testid={`ef-session-${idx}-remove`}
                      >
                        <Trash2 className="w-3 h-3" />Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Mini label="Date">
                        <input
                          type="date"
                          className="form-input h-9 text-sm"
                          value={s.start_date || ""}
                          onChange={(e) => {
                            updateSession(idx, "start_date", e.target.value);
                            // Default end_date to same day if blank
                            if (!s.end_date) updateSession(idx, "end_date", e.target.value);
                          }}
                          data-testid={`ef-session-${idx}-date`}
                        />
                      </Mini>
                      <Mini label="Start Time">
                        <input
                          type="time"
                          className="form-input h-9 text-sm"
                          value={s.start_time || ""}
                          onChange={(e) => updateSession(idx, "start_time", e.target.value)}
                          data-testid={`ef-session-${idx}-start-time`}
                        />
                      </Mini>
                      <Mini label="End Date">
                        <input
                          type="date"
                          className="form-input h-9 text-sm"
                          value={s.end_date || s.start_date || ""}
                          onChange={(e) => updateSession(idx, "end_date", e.target.value)}
                          data-testid={`ef-session-${idx}-end-date`}
                        />
                      </Mini>
                      <Mini label="End Time">
                        <input
                          type="time"
                          className="form-input h-9 text-sm"
                          value={s.end_time || ""}
                          onChange={(e) => updateSession(idx, "end_time", e.target.value)}
                          data-testid={`ef-session-${idx}-end-time`}
                        />
                      </Mini>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-slate-500 italic">No sessions added. Click <b>Add Session</b> to schedule day 1.</div>
            )}
          </div>
        )}

        {/* Single-day scheduling (hidden when multi-session mode is active) */}
        {!hasSessions && (
          <>
            <Field label="Start Date" testid="ef-start-date">
              <input
                type="date"
                className="form-input"
                value={body.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                data-testid="ef-start-date-input"
              />
            </Field>
            <Field label="Start Time" testid="ef-start-time">
              <input
                type="time"
                className="form-input"
                value={body.start_time}
                onChange={(e) => set("start_time", e.target.value)}
                data-testid="ef-start-time-input"
              />
            </Field>
            <Field label="End Date" testid="ef-end-date">
              <input
                type="date"
                className="form-input"
                value={body.end_date}
                onChange={(e) => set("end_date", e.target.value)}
                data-testid="ef-end-date-input"
              />
            </Field>
            <Field label="End Time" testid="ef-end-time">
              <input
                type="time"
                className="form-input"
                value={body.end_time}
                onChange={(e) => set("end_time", e.target.value)}
                data-testid="ef-end-time-input"
              />
            </Field>
          </>
        )}
      </FormSection>

      {/* ─── Instructor & Location ─────────────────────────────────── */}
      <FormSection title="Instructor & Location">
        <Field label="Instructor" testid="ef-instructor">
          <input
            type="text"
            className="form-input"
            value={body.instructor_name}
            onChange={(e) => set("instructor_name", e.target.value)}
            placeholder="Instructor name"
            data-testid="ef-instructor-input"
          />
        </Field>
        <Field label="Location / Meeting URL" testid="ef-location">
          <input
            type="text"
            className="form-input"
            value={body.location}
            onChange={(e) => set("location", e.target.value)}
            placeholder="Venue address OR https://zoom.us/…"
            data-testid="ef-location-input"
          />
        </Field>
      </FormSection>

      {/* ─── Enrollment ────────────────────────────────────────────── */}
      <FormSection title="Enrollment">
        <Field label="Tuition Amount (USD)" testid="ef-price">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <input
              type="number"
              min={0}
              step="0.01"
              className="form-input pl-7"
              value={body.price_usd}
              onChange={(e) => set("price_usd", Number(e.target.value) || 0)}
              data-testid="ef-price-input"
            />
          </div>
        </Field>
        <Field label="Maximum Seats" testid="ef-seats">
          <input
            type="number"
            min={0}
            className="form-input"
            value={body.seat_limit}
            onChange={(e) => set("seat_limit", Number(e.target.value) || 0)}
            placeholder="0 = unlimited"
            data-testid="ef-seats-input"
          />
          <Hint>0 means unlimited seats.</Hint>
        </Field>
        <Field label="Registration Deadline" testid="ef-deadline">
          <input
            type="date"
            className="form-input"
            value={body.registration_deadline}
            onChange={(e) => set("registration_deadline", e.target.value)}
            data-testid="ef-deadline-input"
          />
          <Hint>Optional. Registration closes at end of day.</Hint>
        </Field>
      </FormSection>

      {/* ─── Program Details ───────────────────────────────────────── */}
      <FormSection title="Program Details">
        <Field label="CE Hours" testid="ef-ce-hours">
          <input
            type="number"
            min={0}
            step="0.25"
            className="form-input"
            value={body.ce_hours}
            onChange={(e) => set("ce_hours", Number(e.target.value) || 0)}
            data-testid="ef-ce-hours-input"
          />
        </Field>
        <Field label="Clock Hours" testid="ef-clock-hours">
          <input
            type="number"
            min={0}
            step="0.25"
            className="form-input"
            value={body.clinical_hours}
            onChange={(e) => set("clinical_hours", Number(e.target.value) || 0)}
            data-testid="ef-clock-hours-input"
          />
          <Hint>Clinical / residency clock hours.</Hint>
        </Field>
        <Field label="Track" testid="ef-track">
          <select
            className="form-input"
            value={body.track}
            onChange={(e) => set("track", e.target.value)}
            data-testid="ef-track-select"
          >
            {TRACKS.map((t) => <option key={t.value || "none"} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Includes Kit" testid="ef-kit">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 h-10">
            <input
              type="checkbox"
              checked={!!body.includes_kit}
              onChange={(e) => set("includes_kit", e.target.checked)}
              data-testid="ef-kit-input"
            />
            Clinical kit provided to students
          </label>
        </Field>
      </FormSection>

      {/* ─── Publishing ────────────────────────────────────────────── */}
      <FormSection title="Publishing">
        <Field label="Status" testid="ef-status">
          <div className="flex items-center gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => set("status", s)}
                className={`text-xs px-3 h-8 rounded-full border ${
                  body.status === s
                    ? "border-purple-900 bg-purple-50 text-purple-900 font-medium"
                    : "border-slate-200 text-slate-600 hover:border-purple-300"
                }`}
                data-testid={`ef-status-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          <Hint>
            <b>Draft</b> = admin only · <b>Published</b> = public · <b>Cancelled</b>/<b>Completed</b> = archived
          </Hint>
        </Field>
      </FormSection>
    </div>
  );
}

// Build a backend-ready payload from the form's split-state body
function buildPayloadFromBody(body) {
  const hasSessions = (body.sessions || []).length > 0;
  const sessions = hasSessions
    ? body.sessions.map((s) => ({
        id: s.id,
        label: s.label || "",
        starts_at: joinDateTime(s.start_date, s.start_time),
        ends_at:   joinDateTime(s.end_date || s.start_date, s.end_time),
      }))
    : [];

  // For single-day events, derive starts_at/ends_at from the split fields.
  // For multi-day events, backend will re-derive from sessions automatically.
  const starts_at = hasSessions ? "" : joinDateTime(body.start_date, body.start_time);
  const ends_at   = hasSessions ? "" : joinDateTime(body.end_date || body.start_date, body.end_time);

  return {
    event_type: body.event_type,
    title: body.title.trim(),
    description: body.description || "",
    instructor_name: body.instructor_name || "",
    instructor_email: body.instructor_email || "",
    starts_at,
    ends_at,
    delivery_type: body.delivery_type,
    location: body.location || "",
    meeting_url: body.meeting_url || "",
    seat_limit: Number(body.seat_limit) || 0,
    price_usd: Number(body.price_usd) || 0,
    ce_hours: Number(body.ce_hours) || 0,
    clinical_hours: Number(body.clinical_hours) || 0,
    residency_hours: Number(body.residency_hours) || 0,
    track: body.track || "",
    includes_kit: !!body.includes_kit,
    status: body.status || "draft",
    sessions,
    registration_deadline: body.registration_deadline || "",
  };
}

// Convert a server event document back into form-state (split date/time)
function eventToFormBody(ev) {
  const sessions = (ev.sessions || []).map((s) => ({
    id: s.id || `s-${Math.random()}`,
    label: s.label || "",
    start_date: isoToDate(s.starts_at),
    start_time: isoToTime(s.starts_at),
    end_date: isoToDate(s.ends_at),
    end_time: isoToTime(s.ends_at),
  }));
  return {
    event_type: ev.event_type || "continuing_education",
    title: ev.title || "",
    description: ev.description || "",
    delivery_type: ev.delivery_type || "online_live",
    instructor_name: ev.instructor_name || "",
    instructor_email: ev.instructor_email || "",
    location: ev.location || "",
    meeting_url: ev.meeting_url || "",
    start_date: isoToDate(ev.starts_at),
    start_time: isoToTime(ev.starts_at),
    end_date:   isoToDate(ev.ends_at),
    end_time:   isoToTime(ev.ends_at),
    sessions,
    price_usd: Number(ev.price_usd) || 0,
    seat_limit: Number(ev.seat_limit) || 0,
    registration_deadline: ev.registration_deadline ? isoToDate(ev.registration_deadline) : "",
    ce_hours: Number(ev.ce_hours) || 0,
    clinical_hours: Number(ev.clinical_hours) || 0,
    residency_hours: Number(ev.residency_hours) || 0,
    track: ev.track || "",
    includes_kit: !!ev.includes_kit,
    status: ev.status || "draft",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Create form
// ─────────────────────────────────────────────────────────────────────────────

function NewEventForm({ defaultType, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [body, setBody] = useState(() => blankEvent(defaultType));

  // Whenever the parent's `defaultType` changes, the entire NewEventForm is
  // remounted via React `key`, so this is just a safety net.
  useEffect(() => { setBody(blankEvent(defaultType)); }, [defaultType]);

  const set = (k, v) => setBody((s) => ({ ...s, [k]: v }));

  const reset = () => setBody(blankEvent(defaultType));

  const create = async () => {
    if (!body.title.trim()) return toast.error("Event Name is required");
    setBusy(true);
    try {
      const payload = buildPayloadFromBody(body);
      await universityApi.eventsAdminCreate(payload);
      toast.success(`${EVENT_TYPE_LABEL[body.event_type] || "Event"} created`);
      reset();
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e, "Create failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Create Event</div>
          <div className="text-sm text-slate-700">
            Default type: <b>{EVENT_TYPE_LABEL[defaultType] || defaultType}</b>
            <span className="text-slate-400"> · switch tabs to change</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {open && (
            <button
              type="button"
              onClick={reset}
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="admin-events-clear-new-btn"
            >
              <X className="w-3 h-3" />Clear / New
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="text-xs text-purple-900 inline-flex items-center gap-1 hover:underline"
            data-testid="admin-events-toggle-new-btn"
          >
            {open ? "Cancel" : <><Plus className="w-3 h-3" />New event</>}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-5" data-testid="admin-events-new-form">
          <EventFormBody body={body} set={set} allowSessions />
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { reset(); setOpen(false); }}
              className="rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm px-4 h-10"
              data-testid="admin-events-cancel-create"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={create}
              disabled={busy}
              className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium px-5 h-10 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="admin-events-create-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create Event
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Edit modal
// ─────────────────────────────────────────────────────────────────────────────

function EditEventModal({ initial, onClose, onSaved }) {
  const [body, setBody] = useState(() => eventToFormBody(initial));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setBody((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!body.title.trim()) return toast.error("Event Name is required");
    setBusy(true);
    try {
      const payload = buildPayloadFromBody(body);
      await universityApi.eventsAdminPatch(initial.id, payload);
      toast.success("Changes saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="edit-event-modal">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl my-8">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Edit Event</div>
            <div className="font-display text-lg text-slate-900">{initial.title}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="edit-event-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-5">
          <EventFormBody body={body} set={set} allowSessions />
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm px-4 h-10"
            data-testid="edit-event-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium px-5 h-10 inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="edit-event-save"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Event card / row
// ─────────────────────────────────────────────────────────────────────────────

function EventRow({ event: ev, expanded, onToggle, onEdit, reload }) {
  const [regs, setRegs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(ev.status);

  useEffect(() => {
    if (!expanded) return;
    (async () => {
      setLoading(true);
      try {
        const r = await universityApi.eventsAdminRegistrations(ev.id);
        setRegs(r.data?.items || []);
      } catch (e) { toast.error(formatApiError(e, "Could not load registrations")); }
      finally { setLoading(false); }
    })();
  }, [expanded, ev.id]);

  const saveStatus = async (s) => {
    setStatus(s);
    try { await universityApi.eventsAdminPatch(ev.id, { status: s }); toast.success("Status updated"); reload(); }
    catch (e) { toast.error(formatApiError(e, "Update failed")); setStatus(ev.status); }
  };
  const del = async () => {
    if (!confirm("Delete event and all registrations?")) return;
    try { await universityApi.eventsAdminDelete(ev.id); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e, "Delete failed")); }
  };
  const markAttended = async (regId) => {
    try { await universityApi.eventsAdminMarkAttended(regId); toast.success("Attendance marked"); setRegs(null); onToggle(); onToggle(); }
    catch (e) { toast.error(formatApiError(e, "Failed")); }
  };
  const issueCert = async (regId) => {
    try { const r = await universityApi.eventsAdminIssueCert(regId); toast.success(`Certificate ${r.data?.certificate?.public_id} issued`); }
    catch (e) { toast.error(formatApiError(e, "Cert failed")); }
  };

  const isMulti = (ev.sessions || []).length > 0;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-event-row-${ev.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
            <Calendar className="w-3 h-3" />
            {EVENT_TYPE_LABEL[ev.event_type] || ev.event_type.replace("_", " ")}
            {ev.track && <span className="text-amber-800">· {ev.track.replace("_", " ")}</span>}
            {isMulti && <span className="text-purple-800">· {ev.sessions.length}-Day</span>}
          </div>
          <div className="font-display text-lg text-slate-900 leading-snug truncate">{ev.title}</div>
          <div className="text-xs text-slate-500" data-testid={`admin-event-when-${ev.id}`}>
            {fmtSessionRange(ev)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600 inline-flex items-center gap-1">
            <Users className="w-3 h-3" />{ev.registration_count}{ev.seat_limit ? `/${ev.seat_limit}` : ""}
          </span>
          <select
            value={status}
            onChange={(e) => saveStatus(e.target.value)}
            className="rounded-xl border border-slate-200 px-2 h-8 text-xs"
            data-testid={`admin-event-status-${ev.id}`}
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            onClick={onEdit}
            className="rounded-xl border border-purple-200 text-purple-900 h-8 px-3 text-xs inline-flex items-center gap-1 hover:bg-purple-50"
            data-testid={`admin-event-edit-${ev.id}`}
          >
            <Pencil className="w-3 h-3" />Edit
          </button>
          <button
            onClick={onToggle}
            className="rounded-xl border border-slate-200 h-8 px-3 text-xs"
            data-testid={`admin-event-toggle-${ev.id}`}
          >
            {expanded ? "Hide" : "Manage"}
          </button>
          <button
            onClick={del}
            className="rounded-xl border border-rose-200 text-rose-700 h-8 px-3 text-xs"
            data-testid={`admin-event-delete-${ev.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Registrations</div>
          {loading ? (
            <div className="text-slate-500 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
          ) : !regs || regs.length === 0 ? (
            <div className="text-sm text-slate-500">No registrations yet.</div>
          ) : (
            <div className="rounded-xl border border-slate-100 divide-y divide-slate-100">
              {regs.map((r) => (
                <div key={r.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm" data-testid={`admin-event-reg-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">{r.student_name || r.student_email}</div>
                    <div className="text-[11px] text-slate-500">{r.student_email} · {r.phone || ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">
                      <span className={`inline-block px-2 py-0.5 rounded-full ${r.status === "paid" ? "bg-emerald-100 text-emerald-800" : r.status === "attended" ? "bg-purple-100 text-purple-800" : r.status === "waitlist" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{r.status}</span>
                      {r.waitlist_position > 0 && <span> #{r.waitlist_position}</span>}
                    </div>
                    <div className="mt-1 flex items-center gap-1 justify-end">
                      {r.status !== "attended" && (
                        <button onClick={() => markAttended(r.id)} className="text-[11px] text-purple-900 hover:underline inline-flex items-center gap-1" data-testid={`admin-event-attend-${r.id}`}><CheckCircle2 className="w-3 h-3" />Mark attended</button>
                      )}
                      <button onClick={() => issueCert(r.id)} className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid={`admin-event-cert-${r.id}`}><Award className="w-3 h-3" />Issue cert</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tiny UI helpers
// ─────────────────────────────────────────────────────────────────────────────

function FormSection({ title, children }) {
  return (
    <fieldset className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4" data-testid={`form-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <legend className="px-2 text-[11px] uppercase tracking-[0.16em] text-purple-900 font-semibold">{title}</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </fieldset>
  );
}

function Field({ label, required, children, full, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </label>
      {children}
    </div>
  );
}

function Mini({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Hint({ children }) {
  return <div className="text-[11px] text-slate-500 mt-1">{children}</div>;
}
